import React from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';

import { useFechamentos } from '../../../../../hooks/useFechamentos';
import { formatCNPJ } from '../../../../../utils/FormatFunctions';
import { formatDateBR, formatDays } from '../helpers';
import { FechamentoBadge } from './FechamentoBadge';

interface EmpresaFechamentoModalProps {
  empresaId: number | null;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Params do critério vindos do componente pai. Recebido por prop em vez
   * de chamar `useCriterioFechamento` aqui — Chakra Modal nao desmonta ao
   * fechar (apenas oculta), entao o hook leria o localStorage so na 1a
   * montagem e ficaria desincronizado se o usuario trocar o criterio depois.
   */
  criterioParams: Record<string, string | number>;
}

export const EmpresaFechamentoModal = ({
  empresaId,
  isOpen,
  onClose,
  criterioParams,
}: EmpresaFechamentoModalProps) => {
  const { getEmpresaDetalhe } = useFechamentos();

  const { data, isFetching } = useQuery({
    queryKey: ['fechamento-empresa-detalhe', empresaId, criterioParams],
    queryFn: getEmpresaDetalhe,
    enabled: empresaId !== null && empresaId !== undefined && isOpen,
    staleTime: 2 * 60 * 1000,
  });

  const emp = data?.data?.empresa;
  const isInativa = !!emp?.dt_cli_ate;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Detalhes da empresa</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {isFetching && (
            <Flex justify="center" py={10}>
              <Spinner size="lg" />
            </Flex>
          )}

          {!isFetching && !emp && (
            <Text color="gray.500" py={6} textAlign="center">
              Nenhum dado disponível.
            </Text>
          )}

          {!isFetching && emp && (
            <Stack spacing={4}>
              <Box>
                <Text fontSize="lg" fontWeight="bold">
                  {emp.nome || emp.razao_social || '(sem nome)'}
                </Text>
                {emp.razao_social && emp.razao_social !== emp.nome && (
                  <Text fontSize="sm" color="gray.500">
                    {emp.razao_social}
                  </Text>
                )}
                <Flex gap={2} mt={1.5} wrap="wrap" align="center">
                  <Badge colorScheme="blue">ID {emp.external_id || '-'}</Badge>
                  {isInativa && <Badge colorScheme="red">Inativa</Badge>}
                  {emp.cnpj && (
                    <Text fontSize="xs" color="gray.600">
                      CNPJ: {formatCNPJ(emp.cnpj)}
                    </Text>
                  )}
                  {emp.regime && (
                    <Badge colorScheme="purple" variant="subtle">
                      {emp.regime}
                    </Badge>
                  )}
                  {emp.grupo_de_empresas && (
                    <Badge colorScheme="gray" variant="subtle">
                      {emp.grupo_de_empresas}
                    </Badge>
                  )}
                </Flex>
              </Box>

              <Divider />

              <Flex gap={6} wrap="wrap">
                <Box>
                  <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                    Cliente desde
                  </Text>
                  <Text fontWeight="medium">{formatDateBR(emp.dt_cli_desde)}</Text>
                  <Text fontSize="xs" color="gray.500">
                    há {formatDays(emp.dias_como_cliente)}
                  </Text>
                </Box>
                {isInativa && (
                  <Box>
                    <Text fontSize="xs" color="red.500" textTransform="uppercase">
                      Cliente até
                    </Text>
                    <Text fontWeight="medium" color="red.600">
                      {formatDateBR(emp.dt_cli_ate)}
                    </Text>
                  </Box>
                )}
              </Flex>

              <Divider />

              <Box>
                <Text fontSize="xs" color="gray.500" textTransform="uppercase" mb={2}>
                  Status do fechamento contábil
                </Text>
                <Flex
                  align="center"
                  justify="space-between"
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="gray.50"
                >
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      Contabilidade
                    </Text>
                    {emp.contabil.data_esperada && (
                      <Text fontSize="11px" color="gray.500" mt={0.5}>
                        Esperado: {formatDateBR(emp.contabil.data_esperada)}
                      </Text>
                    )}
                  </Box>
                  <FechamentoBadge info={emp.contabil} size="md" />
                </Flex>
              </Box>
            </Stack>
          )}
        </ModalBody>

        <ModalFooter borderTopWidth="1px">
          <Button size="sm" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
